// react-query layer for RFC-1371 teams.
//
// One module rather than one per resource: members, invites and the team row
// itself all move together — a role change, a removal and a leave each alter
// something the switcher renders. A single prefix invalidation over `['teams']`
// cannot get that wrong, and these lists are small enough that the precision is
// not worth the bug.
//
// Every query is gated on `teams_enabled` through `enabled:` rather than a
// caller-side `if`. With the flag off EVERY teams route answers
// `404 {"detail": {"error": "teams_disabled"}}`, so firing them would be pure
// noise and a signal that the gate leaked.
//
// Deletion previews explain which references block deletion. The server still enforces
// destructive-operation rules at write time and supplies project counts on 409.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'

import {
  clearMemberAllowance,
  listAllowances,
  setMemberAllowance,
  TEAM_CREDITS_ENDPOINT_AVAILABLE,
  changeMemberRole,
  createInvite,
  createTeam,
  deleteTeam,
  getInvitePreview,
  getTeamCredits,
  getTeamUsage,
  getTeamDeletionPreview,
  leaveTeam,
  listInvites,
  listMembers,
  listTeams,
  publishProjectToTeam,
  redeemInvite,
  removeMember,
  revokeInvite,
  updateTeam,
  type CreateInviteResult,
  type InvitableRole,
  type LeaveTeamResult,
  type PrivateProjectDisposition,
  type InvitePreview,
  type RemoveMemberResult,
  type Team,
  type TeamCreditHistory,
  type TeamUsage,
  type TeamDeletionPreview,
  type TeamInvite,
  type TeamMember,
  type AllowanceLimitType,
  type AllowancePeriod,
  type MemberAllowance,
  type TeamRole,
} from '../api/teams'
import { useTeamsEnabled } from '../features/teams/useTeamsEnabled'

const TEAMS_KEY: readonly ['teams'] = ['teams']

export function useTeamDeletionPreview(teamId: string) {
  const enabled = useTeamsEnabled()
  return useQuery<TeamDeletionPreview>({
    queryKey: ['teams', teamId, 'deletion-preview'],
    queryFn: () => getTeamDeletionPreview(teamId),
    enabled,
    staleTime: 0,
  })
}

function membersKey(teamId: string): readonly string[] {
  return ['teams', teamId, 'members']
}

function invitesKey(teamId: string): readonly string[] {
  return ['teams', teamId, 'invites']
}

function creditsKey(userId: string | undefined, teamId: string): readonly string[] {
  return ['teams', userId ?? 'anonymous', teamId, 'credits']
}

export function teamUsageKey(teamId: string, projectId?: string): readonly string[] {
  return ['teams', teamId, 'usage', projectId ?? 'all']
}

function allowancesKey(teamId: string): readonly string[] {
  return ['teams', teamId, 'allowances']
}

/**
 * The existing project query key.
 *
 * Visibility rides on `GET /projects/{id}` rather than a dedicated route, so
 * publishing invalidates the PROJECT — mirroring `queryKeys.project` in
 * api/hooks.ts. Kept as a local helper rather than imported to avoid a cycle
 * between the teams hooks and the general api hooks.
 */
function projectKey(projectId: string): readonly string[] {
  return ['project', projectId]
}

/**
 * Invalidate everything under `['teams', …]`.
 *
 * Deliberately broad, because the narrow version has an obvious failure mode:
 * removing a member changes the member list, the team's `member_count` in the
 * switcher, and — if it was you — which teams you are in at all.
 */
function useInvalidateTeams(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: TEAMS_KEY })
    void queryClient.invalidateQueries({ queryKey: ['project'] })
    queryClient.removeQueries({ queryKey: ['sparks'] })
    queryClient.removeQueries({ queryKey: ['costSummary'] })
    queryClient.removeQueries({ queryKey: ['generation-trial-status'] })
  }
}

/** Every team the user belongs to, each carrying THEIR role (`my_role`). */
export function useMyTeams() {
  const enabled = useTeamsEnabled()
  const { user, isLoading: authLoading } = useAuth()
  return useQuery<Team[]>({
    // A forced flag can precede session restoration. Never cache a guest read
    // or carry one account's membership list into another account's session.
    queryKey: [...TEAMS_KEY, 'mine', user?.id ?? null],
    queryFn: listTeams,
    enabled: enabled && !authLoading && user !== null,
    staleTime: 30_000,
  })
}

export function useTeamMembers(teamId: string | null) {
  const enabled = useTeamsEnabled()
  return useQuery<TeamMember[]>({
    queryKey: membersKey(teamId ?? ''),
    queryFn: () => listMembers(teamId ?? ''),
    enabled: enabled && teamId !== null,
    staleTime: 30_000,
  })
}

export function useTeamInvites(teamId: string | null, active: boolean) {
  const enabled = useTeamsEnabled()
  return useQuery<TeamInvite[]>({
    queryKey: invitesKey(teamId ?? ''),
    queryFn: () => listInvites(teamId ?? ''),
    // `active` scopes this to an open invite panel. An invite list is
    // administrative detail nobody needs fetched because the switcher rendered.
    enabled: enabled && teamId !== null && active,
    staleTime: 15_000,
  })
}

/**
 * Team balance + one page of credit history (RFC-1372 C9). `page` is 0-based,
 * newest first.
 *
 * Permanently disabled until the endpoint exists — see
 * TEAM_CREDITS_ENDPOINT_AVAILABLE. Not role-gated by design: members can
 * already see team spend, and hiding the credits-in side leaves a pool whose
 * balance cannot be reconciled from anything they may look at.
 */
export function useTeamCredits(teamId: string | null, active: boolean, page = 0) {
  const enabled = useTeamsEnabled()
  const { user } = useAuth()
  return useQuery<TeamCreditHistory>({
    queryKey: [...creditsKey(user?.id, teamId ?? ''), page],
    queryFn: () => getTeamCredits(teamId ?? '', page),
    enabled: TEAM_CREDITS_ENDPOINT_AVAILABLE && enabled && teamId !== null && active,
    staleTime: 15_000,
    // Keep the current page on screen while the next one loads, instead of
    // flashing the loading state between pages. Same team only: another
    // team's balance and another user's financial access must never stand in
    // for the current request.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === (user?.id ?? 'anonymous') &&
      previousQuery.queryKey[2] === teamId
        ? keepPreviousData(previous)
        : undefined,
  })
}

export function useTeamUsage(
  teamId: string | null,
  active: boolean,
  projectId?: string,
) {
  const enabled = useTeamsEnabled()
  return useQuery<TeamUsage>({
    queryKey: teamUsageKey(teamId ?? '', projectId),
    queryFn: () => getTeamUsage(teamId ?? '', projectId),
    enabled: enabled && active && teamId !== null,
    staleTime: 15_000,
  })
}

/**
 * Look at an invite before accepting it, so the join page can name the team.
 *
 * `retry: false` and a failure that the caller IGNORES: this is decoration on
 * a page whose actual job is redemption. A preview that 404s or drifts must
 * never stop a token that would redeem fine.
 */
export function useInvitePreview(token: string | undefined) {
  return useQuery<InvitePreview>({
    queryKey: ['teams', 'invite-preview', token ?? ''],
    queryFn: () => getInvitePreview(token ?? ''),
    enabled: token !== undefined && token !== '',
    retry: false,
    staleTime: 60_000,
  })
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useCreateTeam() {
  const invalidate = useInvalidateTeams()
  return useMutation<Team, Error, string>({
    mutationFn: (name) => createTeam(name),
    onSuccess: invalidate,
  })
}

export function useRenameTeam() {
  const invalidate = useInvalidateTeams()
  return useMutation<Team, Error, { teamId: string; name: string }>({
    mutationFn: (vars) => updateTeam(vars.teamId, { name: vars.name }),
    onSuccess: invalidate,
  })
}

export function useChangeMemberRole() {
  const invalidate = useInvalidateTeams()
  return useMutation<void, Error, { teamId: string; userId: string; role: TeamRole }>({
    mutationFn: (vars) => changeMemberRole(vars.teamId, vars.userId, vars.role),
    onSuccess: invalidate,
  })
}

export function useRemoveMember() {
  const invalidate = useInvalidateTeams()
  return useMutation<RemoveMemberResult, Error, { teamId: string; userId: string }>({
    mutationFn: (vars) => removeMember(vars.teamId, vars.userId),
    onSuccess: invalidate,
  })
}

export function useCreateInvite() {
  const invalidate = useInvalidateTeams()
  return useMutation<
    CreateInviteResult,
    Error,
    { teamId: string; roleOnJoin: InvitableRole; expiresInDays: number; maxUses: number | null }
  >({
    mutationFn: (vars) =>
      createInvite(vars.teamId, {
        roleOnJoin: vars.roleOnJoin,
        expiresInDays: vars.expiresInDays,
        maxUses: vars.maxUses,
      }),
    onSuccess: invalidate,
  })
}

export function useRevokeInvite() {
  const invalidate = useInvalidateTeams()
  return useMutation<void, Error, { teamId: string; inviteId: string }>({
    mutationFn: (vars) => revokeInvite(vars.teamId, vars.inviteId),
    onSuccess: invalidate,
  })
}

/**
 * Leave a team.
 *
 * `privateProjects` is null on the first attempt; a 409 carrying the project
 * list is the prompt, and the second call supplies the answer. Deliberately NOT
 * wrapped in a retry-on-409 helper — the whole point is that a human answers
 * in between.
 */
export function useLeaveTeam() {
  const invalidate = useInvalidateTeams()
  return useMutation<
    LeaveTeamResult,
    Error,
    { teamId: string; privateProjects: PrivateProjectDisposition | null }
  >({
    mutationFn: (vars) => leaveTeam(vars.teamId, vars.privateProjects),
    onSuccess: invalidate,
  })
}

export function useDeleteTeam() {
  const invalidate = useInvalidateTeams()
  return useMutation<void, Error, string>({
    mutationFn: (teamId) => deleteTeam(teamId),
    onSuccess: invalidate,
  })
}

/** Redeem an invite token. Idempotent server-side. */
export function useRedeemInvite() {
  const invalidate = useInvalidateTeams()
  return useMutation({
    mutationFn: (token: string) => redeemInvite(token),
    onSuccess: invalidate,
  })
}

/**
 * Publish a project to its owning team (D3).
 *
 * Invalidates the teams tree as well as the project's visibility: publishing
 * changes what every other member can see, and the project counts behind a
 * blocked team delete are derived from exactly that.
 */
export function usePublishProjectToTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => publishProjectToTeam(projectId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: projectKey(result.projectId) })
      void queryClient.invalidateQueries({ queryKey: TEAMS_KEY })
      // The dashboard lists too: publishing MOVES the project between the two
      // D3 tabs, so both the Private list it leaves and the Team list it joins
      // are now stale. Without this the project sits on the Private tab until
      // something else happens to refetch, and the move looks like it failed —
      // on an action that cannot be retried, because it already succeeded.
      //
      // The bare prefix matches every (tab, page, sort, prefix, team) entry;
      // enumerating them would silently miss whichever the user is on.
      void queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
    },
  })
}


// ── Member spending caps (RFC-1372 C3) ─────────────────────────────────────

/**
 * Every cap set in the team. Owner/Admin only.
 *
 * `active` gates the fetch the same way `useTeamInvites` does, so the settings
 * panel does not request management data for a tab nobody has opened — and a
 * Member never fires a request that can only 403.
 */
export function useTeamAllowances(teamId: string | null, active: boolean) {
  const enabled = useTeamsEnabled()
  return useQuery<MemberAllowance[], Error>({
    queryKey: allowancesKey(teamId ?? ''),
    queryFn: () => listAllowances(teamId ?? ''),
    // `as` is banned in this codebase, so the null is narrowed with ??
    // and the query is simply never enabled for an empty id.
    enabled: enabled && active && teamId !== null,
  })
}

export function useSetMemberAllowance() {
  const queryClient = useQueryClient()
  return useMutation<
    MemberAllowance,
    Error,
    {
      teamId: string
      userId: string
      limitType: AllowanceLimitType
      limitAmount: number | null
      period: AllowancePeriod | null
    }
  >({
    mutationFn: (vars) =>
      setMemberAllowance(vars.teamId, vars.userId, {
        limitType: vars.limitType,
        limitAmount: vars.limitAmount,
        period: vars.period,
      }),
    onSuccess: (_result, vars) => {
      void queryClient.invalidateQueries({ queryKey: allowancesKey(vars.teamId) })
      // The caller's own spendable balance changes when THEY are the target, and
      // the balance widget reads a different key.
      void queryClient.invalidateQueries({ queryKey: ['sparks'] })
    },
  })
}

export function useClearMemberAllowance() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { teamId: string; userId: string }>({
    mutationFn: (vars) => clearMemberAllowance(vars.teamId, vars.userId),
    onSuccess: (_result, vars) => {
      void queryClient.invalidateQueries({ queryKey: allowancesKey(vars.teamId) })
      void queryClient.invalidateQueries({ queryKey: ['sparks'] })
    },
  })
}
